import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { CampusesAPI, InstitutionsAPI, TenantsAPI } from "@/lib/sprint2-api";
import { NoticesAPI } from "@/lib/sprint7-api"; // reuse GridFS for the campus photo
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, Camera, Building2, MapPin, Mail, Phone, Globe, Edit3, Loader2, Save } from "lucide-react";

export default function CampusDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const tQ = useQuery({ queryKey: ["cdetail-tenants"], queryFn: TenantsAPI.list, enabled: isSuper });
  const tenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const tParam = tenantId ? { tenant_id: tenantId } : {};

  const cQ = useQuery({
    queryKey: ["campus-detail", id, tenantId],
    queryFn: () => CampusesAPI.list(tParam).then((all) => all.find((c) => c.id === id)),
    enabled: !!tenantId,
  });
  const statsQ = useQuery({
    queryKey: ["campus-stats-one", id, tenantId],
    queryFn: () => CampusesAPI.stats(tParam).then((all) => all.find((s) => s.id === id)),
    enabled: !!tenantId,
  });
  const instQ = useQuery({
    queryKey: ["cdetail-institutions", tenantId],
    queryFn: () => InstitutionsAPI.list(tParam),
    enabled: !!tenantId,
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);

  const c = { ...cQ.data, ...form };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: typeof e === "string" ? e : e.target.value }));

  const inst = instQ.data?.find((i) => i.id === c.institution_id);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await NoticesAPI.uploadAttachment(file);
      setForm((p) => ({ ...p, photo_url: NoticesAPI.attachmentUrl(res.id) }));
      toast.success("Campus photo uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setUploading(false); }
  };

  const save = useMutation({
    mutationFn: () => CampusesAPI.update(id, {
      name: c.name, code: c.code, address: c.address, city: c.city, state: c.state,
      country: c.country, phone: c.phone, email: c.email, website: c.website,
      photo_url: c.photo_url,
    }),
    onSuccess: () => {
      toast.success("Campus updated");
      qc.invalidateQueries({ queryKey: ["campus-detail"] });
      qc.invalidateQueries({ queryKey: ["campuses"] });
      setEditing(false);
      setForm({});
    },
    onError: (e) => toast.error(formatApiErrorDetail(e)),
  });

  if (cQ.isLoading) {
    return <AppShell><div className="p-10 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div></AppShell>;
  }
  if (!cQ.data) {
    return <AppShell><div className="p-10 text-center text-rose-600">Campus not found.</div></AppShell>;
  }

  const s = statsQ.data;
  const placeholder = "https://images.unsplash.com/photo-1562774053-701939374585?w=1200&q=70";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6" data-testid="campus-detail-page">
        <Button variant="ghost" onClick={() => navigate("/campuses")} className="mb-4 -ml-2"><ChevronLeft className="h-4 w-4 mr-1" /> Back to Campuses</Button>

        {/* Hero with photo */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm mb-6">
          <div className="relative w-full bg-gradient-to-br from-slate-900 to-slate-700 group" style={{ aspectRatio: "16/9" }}>
            <img
              src={c.photo_url || placeholder}
              alt={c.name}
              className="absolute inset-0 w-full h-full object-contain bg-black/30"
              data-testid="campus-hero-img"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-6 right-6 text-white flex items-end justify-between gap-3 flex-wrap">
              <div>
                <Badge variant="outline" className="text-[10px] mb-1 bg-white/15 text-white border-white/30">{inst?.name || "—"}</Badge>
                <h1 className="font-display font-extrabold text-3xl tracking-tight drop-shadow">{c.name}</h1>
                <div className="text-sm text-white/80 mt-1">{[c.address, c.city, c.state].filter(Boolean).join(", ") || "Address not set"}</div>
              </div>
              {editing && (
                <label className="cursor-pointer">
                  <div className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-xs font-medium flex items-center gap-1.5 hover:bg-white/25 transition">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                    Change photo
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </div>

          <div className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <Building2 className="h-3.5 w-3.5" />{c.code || "—"}
            </div>
            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <Button variant="ghost" onClick={() => { setEditing(false); setForm({}); }}>Cancel</Button>
                  <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="campus-save">
                    {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="campus-edit-btn"><Edit3 className="h-4 w-4 mr-2" />Edit details</Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        {s && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: "Students", v: s.students, c: "emerald" },
              { label: "Classes", v: s.classes, c: "sky" },
              { label: "Sections", v: s.sections, c: "amber" },
            ].map((k, i) => (
              <div key={k.label} className={`bg-card border border-border rounded-xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-1`} style={{ animationDelay: `${i*60}ms` }}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
                <div className="font-display font-extrabold text-3xl mt-1">{k.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Details grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-lg mb-3">Address & Contact</h3>
            {editing ? (
              <div className="space-y-2.5">
                <div><Label>Address</Label><Input value={c.address || ""} onChange={set("address")} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>City</Label><Input value={c.city || ""} onChange={set("city")} /></div>
                  <div><Label>State</Label><Input value={c.state || ""} onChange={set("state")} /></div>
                </div>
                <div><Label>Country</Label><Input value={c.country || ""} onChange={set("country")} /></div>
                <div><Label>Phone</Label><Input value={c.phone || ""} onChange={set("phone")} /></div>
                <div><Label>Email</Label><Input value={c.email || ""} onChange={set("email")} type="email" /></div>
                <div><Label>Website</Label><Input value={c.website || ""} onChange={set("website")} placeholder="https://…" /></div>
              </div>
            ) : (
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /> {[c.address, c.city, c.state, c.country].filter(Boolean).join(", ") || <span className="text-muted-foreground italic">—</span>}</li>
                <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground shrink-0" /> {c.phone || <span className="text-muted-foreground italic">—</span>}</li>
                <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground shrink-0" /> {c.email || <span className="text-muted-foreground italic">—</span>}</li>
                <li className="flex items-center gap-2"><Globe className="h-4 w-4 text-muted-foreground shrink-0" /> {c.website ? <a href={c.website} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{c.website}</a> : <span className="text-muted-foreground italic">—</span>}</li>
              </ul>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
            <h3 className="font-semibold text-lg mb-3">Identity</h3>
            {editing ? (
              <div className="space-y-2.5">
                <div><Label>Campus name</Label><Input value={c.name || ""} onChange={set("name")} /></div>
                <div><Label>Code</Label><Input value={c.code || ""} onChange={set("code")} /></div>
              </div>
            ) : (
              <ul className="space-y-3 text-sm">
                <li className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-semibold">{c.name}</span></li>
                <li className="flex justify-between"><span className="text-muted-foreground">Code</span><span className="font-mono font-semibold">{c.code || "—"}</span></li>
                <li className="flex justify-between"><span className="text-muted-foreground">Institution</span><span className="font-semibold">{inst?.name || "—"}</span></li>
                <li className="flex justify-between"><span className="text-muted-foreground">Status</span>
                  <Badge variant="outline" className={c.is_active === false ? "" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{c.is_active === false ? "inactive" : "active"}</Badge>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
